import React, { useEffect, useState, useMemo } from 'react'
import { useMutation } from '@apollo/client'
import {
  ArrowRightOutlined,
  CaretDownOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import {
  Button,
  Col,
  Form,
  Row,
  Slider,
  message,
  Tooltip,
  InputNumber,
  Input,
  Divider,
  Dropdown
} from 'antd'
import { Store, Merchant } from '@slerp/controls'
import { UPDATE_STORE_PREPARATION_TIME } from './actions'
import styled from '@emotion/styled'
import {
  validateDynamicPreparationTimeBands,
  validateLowerThreshold,
  validateUpperThreshold,
  validateAdditionalPrepTime
} from './rules'
import SectionTitle from 'components/Common/SectionTitle'

interface PreparationTimeProps {
  disabled: boolean
  store: Store
  merchant: Merchant
}

export interface TimeBand {
  lower_threshold?: number | null
  upper_threshold?: number | null
  upper_threshold_type?: 'and_above' | 'manual_entry'
  extra_minutes?: number | null
}

interface FormValues {
  quiet_preparation_time: number | 5
  moderate_preparation_time: number | 5
  busy_preparation_time: number | 5
  dynamic_preparation_time_bands?: TimeBand[]
}

interface ActivityModeConfig {
  key: string
  label: string
  testId: string
  tooltip?: string
  className?: string
}

// Configuration objects
const ACTIVITY_MODE_CONFIG: ActivityModeConfig[] = [
  {
    key: 'quiet_preparation_time',
    label: 'Quiet',
    testId: 'quiet-preparation-time-slider'
  },
  {
    key: 'moderate_preparation_time',
    label: 'Moderate',
    testId: 'moderate-preparation-time-slider'
  },
  {
    key: 'busy_preparation_time',
    label: 'Busy',
    testId: 'busy-preparation-time-slider',
    tooltip:
      'When Busy Mode is activated, the Busy Mode Delivery Zone may apply. See the Delivery tab to set up.',
    className: '_mb-72'
  }
]

const SLIDER_CONFIG = {
  marks: { 5: '5', 15: '15', 30: '30', 45: '45', 60: '60' },
  step: 5,
  min: 5,
  max: 60,
  className: '_ml-24 _mr-24'
} as const

const FORM_LAYOUT = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 }
} as const

// Reusable ActivityModeSlider component
interface ActivityModeSliderProps {
  name: string
  label: string
  tooltip?: string
  className?: string
  testId: string
  disabled: boolean
}

const ActivityModeSlider: React.FC<ActivityModeSliderProps> = ({
  name,
  label,
  tooltip,
  className,
  testId,
  disabled
}) => (
  <StyledFormItemSlider
    name={name}
    className={className}
    label={
      <Form.Item
        shouldUpdate={(prev: any, curr: any) => prev[name] !== curr[name]}
      >
        {({ getFieldValue }: { getFieldValue: (name: string) => any }) => (
          <>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {label}
              {tooltip && (
                <Tooltip zIndex={9999999} title={tooltip}>
                  <QuestionCircleOutlined className='_ml-4' />
                </Tooltip>
              )}
            </span>
            <DefaultPrepTimeContainer>
              <span>Default prep time</span>
              <Input disabled value={getFieldValue(name)} suffix='min' />
            </DefaultPrepTimeContainer>
          </>
        )}
      </Form.Item>
    }
  >
    <StyledSlider {...SLIDER_CONFIG} disabled={disabled} data-testid={testId} />
  </StyledFormItemSlider>
)

interface DynamicBandFieldProps {
  field: any
  fieldName: string
  label?: string
  children: React.ReactNode
  rules?: any[]
  className?: string
}

const DynamicBandField: React.FC<DynamicBandFieldProps> = ({
  field,
  fieldName,
  label,
  children,
  rules = [],
  className = '_mb-0'
}) => (
  <Form.Item
    key={field.key}
    {...(field.name === 0 && label ? { label } : { label: '' })}
    className={className}
    name={[field.name, fieldName]}
    rules={rules}
    validateTrigger={['onBlur']}
    labelCol={{ span: 24 }}
  >
    {children}
  </Form.Item>
)

const PreparationTime = (props: PreparationTimeProps) => {
  const { disabled = false, store } = props
  const [form] = Form.useForm()
  const [forceRender, setForceRender] = useState(false)

  const dynamicPreparationTimeBands = store?.dynamic_preparation_time_bands

  const initialFormValues = useMemo(
    () => ({
      quiet_preparation_time: store?.settings.quiet_preparation_time,
      moderate_preparation_time: store?.settings.moderate_preparation_time,
      busy_preparation_time: store?.settings.busy_preparation_time,
      dynamic_preparation_time_bands: dynamicPreparationTimeBands.map(
        (band, index) => {
          const isLastBand = dynamicPreparationTimeBands.length === index + 1
          return {
            ...band,
            lower_threshold: band.lower_threshold?.toString(),
            upper_threshold: band.upper_threshold?.toString(),
            extra_minutes: band.extra_minutes?.toString(),
            upper_threshold_type: isLastBand ? 'and_above' : null
          }
        }
      )
    }),
    [store, dynamicPreparationTimeBands]
  )

  const [updateStorePreparationTime, { loading }] = useMutation(
    UPDATE_STORE_PREPARATION_TIME,
    { fetchPolicy: 'no-cache' }
  )

  const onFinish = (values: FormValues) => {
    const {
      quiet_preparation_time,
      moderate_preparation_time,
      busy_preparation_time,
      dynamic_preparation_time_bands
    } = values

    const mappedDynamicPreparationTimeBands =
      dynamic_preparation_time_bands?.map((band) => ({
        lowerThreshold: (+band.lower_threshold!).toFixed(2),
        upperThreshold:
          band.upper_threshold_type === 'and_above'
            ? null
            : (+band.upper_threshold!).toFixed(2),
        extraMinutes: +band.extra_minutes!
      })) ?? []

    const fieldsToValidate = [
      'quiet_preparation_time',
      'moderate_preparation_time',
      'busy_preparation_time',
      ...(dynamic_preparation_time_bands?.length
        ? ['dynamic_preparation_time_bands']
        : [])
    ]

    form
      .validateFields(fieldsToValidate)
      .then(() => {
        message.loading('Updating... Please wait.')

        const updateParams = {
          dynamicPreparationTimeBands: mappedDynamicPreparationTimeBands,
          settings: {
            quietPreparationTime: quiet_preparation_time,
            moderatePreparationTime: moderate_preparation_time,
            busyPreparationTime: busy_preparation_time
          }
        }

        return updateStorePreparationTime({
          variables: { storeId: store.id, params: updateParams }
        })
      })
      .then(() => {
        message.destroy()
        message.success('Store Updated', 1)
      })
      .catch((error: any) => {
        message.error(`Unable to update settings due to: ${error}`, 5)
      })
  }

  // Handle automatic threshold filling
  useEffect(() => {
    const timeBands = form.getFieldValue('dynamic_preparation_time_bands') ?? []
    const previousRow = timeBands[timeBands.length - 2]
    const previousRowIndex = timeBands.length - 2

    if (timeBands.length > 1) {
      form.setFieldsValue({
        dynamic_preparation_time_bands: {
          [timeBands.length - 1]: {
            lower_threshold: (previousRow ?? {})?.upper_threshold ?? 0
          }
        }
      })

      // Validate only the previous row's fields
      form.validateFields([
        ['dynamic_preparation_time_bands', previousRowIndex, 'lower_threshold'],
        ['dynamic_preparation_time_bands', previousRowIndex, 'upper_threshold'],
        ['dynamic_preparation_time_bands', previousRowIndex, 'extra_minutes']
      ])
    }
  }, [forceRender, form])

  const isLastRow = (fieldName: number, fieldLength: number) =>
    fieldLength >= 1 && fieldName === fieldLength - 1

  const renderActivityModeSliders = () =>
    ACTIVITY_MODE_CONFIG.map((config) => (
      <ActivityModeSlider
        key={config.key}
        name={config.key}
        label={config.label}
        tooltip={config.tooltip}
        className={config.className}
        testId={config.testId}
        disabled={disabled}
      />
    ))

  const renderUpperThresholdField = (field: any, fields: any[], form: any) => {
    const fieldName = field.name
    const isLast = isLastRow(fieldName, fields.length)

    return (
      <Form.Item
        shouldUpdate={(prev: any, curr: any) => {
          const prevType =
            prev.dynamic_preparation_time_bands[fieldName]?.upper_threshold_type
          const currType =
            curr.dynamic_preparation_time_bands[fieldName]?.upper_threshold_type
          return prevType !== currType
        }}
        className='_mb-0'
        labelCol={{ span: 24 }}
      >
        {({ getFieldValue }: { getFieldValue: (name: string) => any }) => {
          const timeBands =
            getFieldValue('dynamic_preparation_time_bands') || []
          const upperThresholdType =
            timeBands[fields.length - 1]?.upper_threshold_type

          return isLast && upperThresholdType === 'and_above' ? (
            <DynamicBandField
              field={field}
              fieldName='upper_threshold'
              label={fieldName === 0 ? 'Upper basket limit' : undefined}
            >
              <Input
                disabled
                placeholder='And Above'
                style={{ height: '38px' }}
              />
            </DynamicBandField>
          ) : (
            <DynamicBandField
              field={field}
              fieldName='upper_threshold'
              label={fieldName === 0 ? 'Upper basket limit' : undefined}
              rules={[validateUpperThreshold(form, fieldName)]}
            >
              <StyledInputNumber
                min={0.01}
                precision={2}
                disabled={disabled}
                prefix={<span>£</span>}
                onBlur={() =>
                  form.validateFields(['dynamic_preparation_time_bands'])
                }
              />
            </DynamicBandField>
          )
        }}
      </Form.Item>
    )
  }

  const renderThresholdControls = (field: any, fields: any[]) => {
    const fieldName = field.name
    const isLast = isLastRow(fieldName, fields.length)

    if (!isLast) return null

    const currentType = form.getFieldValue('dynamic_preparation_time_bands')?.[
      fieldName
    ]?.upper_threshold_type

    return (
      <InputNumberSuffix fieldName={fieldName}>
        <Form.Item className='_mb-0' name={[fieldName, 'upper_threshold_type']}>
          {currentType === 'and_above' ? (
            <Button
              onClick={() => {
                form.setFieldsValue({
                  dynamic_preparation_time_bands: {
                    [fieldName]: {
                      upper_threshold_type: null,
                      upper_threshold: null
                    }
                  }
                })
              }}
              icon={
                <CloseOutlined
                  style={{
                    position: 'relative',
                    top: '0.5px',
                    left: '12px',
                    width: '12px',
                    height: '12px'
                  }}
                />
              }
              type='text'
            />
          ) : (
            <Dropdown
              disabled={fields.length > 1 && fieldName !== fields.length - 1}
              menu={{
                items: [
                  ...(fields.length === 0 || fieldName === fields.length - 1
                    ? [{ key: 'and_above', label: 'And above' }]
                    : [])
                ],
                selectable: true,
                onClick: (e: any) => {
                  form.setFieldsValue({
                    dynamic_preparation_time_bands: {
                      [fieldName]: {
                        upper_threshold_type: e.key,
                        upper_threshold:
                          e.key === 'and_above'
                            ? null
                            : form.getFieldValue(
                                'dynamic_preparation_time_bands'
                              )?.[fieldName]?.upper_threshold
                      }
                    }
                  })
                }
              }}
              placement='bottomRight'
            >
              <Button icon={<CaretDownOutlined />} type='text' />
            </Dropdown>
          )}
        </Form.Item>
      </InputNumberSuffix>
    )
  }

  return (
    <Form
      data-testid='preparation-time-form'
      {...FORM_LAYOUT}
      form={form}
      initialValues={initialFormValues}
      onFinish={(values: unknown) => onFinish(values as FormValues)}
    >
      <SectionTitle title='Activity Mode' className='_mb-24' />
      {renderActivityModeSliders()}

      <Form.List
        name='dynamic_preparation_time_bands'
        rules={[validateDynamicPreparationTimeBands(form)]}
      >
        {(
          fields: any,
          { add, remove }: { add: () => void; remove: (name: number) => void },
          { errors }: { errors: any }
        ) => (
          <>
            <Row justify='space-between' align='middle' className='_mb-24'>
              <SectionTitle title='Additional Preparation Time' />
              <StyledFormItem
                shouldUpdate={(prev: any, curr: any) => {
                  const prevTimeBands =
                    prev.dynamic_preparation_time_bands || []
                  const currTimeBands =
                    curr.dynamic_preparation_time_bands || []
                  const prevType =
                    prevTimeBands[fields.length - 1]?.upper_threshold_type
                  const currType =
                    currTimeBands[fields.length - 1]?.upper_threshold_type

                  return (
                    prevTimeBands.length !== currTimeBands.length ||
                    prevType !== currType
                  )
                }}
              >
                {({
                  getFieldValue
                }: {
                  getFieldValue: (name: string) => any
                }) => {
                  const timeBands =
                    getFieldValue('dynamic_preparation_time_bands') || []
                  const upperThresholdType =
                    timeBands[fields.length - 1]?.upper_threshold_type

                  return (
                    <Button
                      onClick={() => {
                        add()
                        setForceRender(!forceRender)
                      }}
                      icon={<PlusCircleOutlined />}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        letterSpacing: '0.5px'
                      }}
                      disabled={
                        errors.length > 0 || upperThresholdType === 'and_above'
                      }
                    >
                      Add New Rule
                    </Button>
                  )
                }}
              </StyledFormItem>
            </Row>

            <Form.Item
              shouldUpdate={(prev: any, curr: any) =>
                prev.dynamic_preparation_time_bands.length !==
                curr.dynamic_preparation_time_bands.length
              }
              className='_mb-0'
            >
              {({
                getFieldValue
              }: {
                getFieldValue: (name: string) => any
              }) => {
                const timeBands =
                  getFieldValue('dynamic_preparation_time_bands') || []

                return timeBands.length > 0 ? (
                  fields.map((field: any) => (
                    <StyledRow key={field.key} justify='space-between'>
                      <Col span={6}>
                        <DynamicBandField
                          field={field}
                          fieldName='lower_threshold'
                          label={
                            field.name === 0 ? 'Lower basket limit' : undefined
                          }
                          rules={[validateLowerThreshold(form, field.name)]}
                        >
                          <StyledInputNumber
                            min={0.01}
                            precision={2}
                            disabled={disabled}
                            prefix={<span>£</span>}
                            onBlur={() =>
                              form.validateFields([
                                'dynamic_preparation_time_bands'
                              ])
                            }
                          />
                        </DynamicBandField>
                      </Col>

                      <Col span={6}>
                        <InputNumberContainer>
                          {renderUpperThresholdField(field, fields, form)}
                          {renderThresholdControls(field, fields)}
                        </InputNumberContainer>
                      </Col>

                      <Col>
                        <Form.Item key={field.key} className='_mb-0'>
                          <ArrowRightOutlined
                            style={{
                              fontSize: 16,
                              position: 'relative',
                              top: field.name === 0 ? '44px' : '6px'
                            }}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={6}>
                        <DynamicBandField
                          field={field}
                          fieldName='extra_minutes'
                          label={
                            field.name === 0
                              ? 'Additional prep time'
                              : undefined
                          }
                          rules={[validateAdditionalPrepTime()]}
                        >
                          <StyledSuffixedInput
                            type='number'
                            disabled={disabled}
                            suffix='min'
                            onBlur={() =>
                              form.validateFields([
                                'dynamic_preparation_time_bands'
                              ])
                            }
                          />
                        </DynamicBandField>
                      </Col>

                      <Col>
                        <Form.Item key={field.key} className='_mb-0'>
                          <StyledButton
                            onClick={() => remove(field.name)}
                            icon={
                              <CloseCircleOutlined
                                style={{
                                  fontSize: 20,
                                  width: '20px',
                                  position: 'relative',
                                  top: field.name === 0 ? '40px' : '2px'
                                }}
                              />
                            }
                            ghost
                          />
                        </Form.Item>
                      </Col>
                    </StyledRow>
                  ))
                ) : (
                  <NoRules>
                    <h3>You haven't created any rules yet.</h3>
                    <p>
                      Once you create an additional preparation time rule, it
                      will appear here.
                    </p>
                  </NoRules>
                )
              }}
            </Form.Item>
          </>
        )}
      </Form.List>

      <Divider />
      <Row className='_mt-32' justify='end'>
        <Col>
          <Button
            title='Save'
            htmlType='submit'
            className='_center-vertical _ml-auto'
            disabled={disabled}
            loading={loading}
            data-testid='preparation-time-save-btn'
            style={{ letterSpacing: '0.5px' }}
          >
            Save
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

const StyledRow = styled(Row)`
  display: flex;
  align-items: flex-start;

  .ant-row.ant-form-item-row {
    margin-bottom: 0;
  }

  .ant-input {
    border: none;
  }
`

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 0;
`

const StyledFormItemSlider = styled(Form.Item)`
  &.ant-form-item {
    margin-bottom: 40px;
  }

  .ant-form-item-control-input-content span:first-of-type {
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
  }

  .ant-col.ant-form-item-label {
    padding: 2px 0;
  }

  .ant-col.ant-form-item-label .ant-form-item,
  .ant-col.ant-form-item-label .ant-row.ant-form-item-row {
    width: 100%;
    margin-bottom: 0;
  }

  .ant-col.ant-form-item-label label {
    width: 100%;
  }

  .ant-col.ant-form-item-label label .ant-form-item-control-input-content {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .ant-input-affix-wrapper.ant-input-affix-wrapper-disabled {
    width: 72px;
    padding: 6px 12px;

    border: none;
    color: #4a4a68;

    .ant-input.ant-input-disabled {
      position: relative;
      top: 0.5px;

      padding: 0;

      color: #4a4a68;
      font-size: 14px;
      line-height: 1;
      text-align: right;
    }

    span.ant-input-suffix {
      padding: 0 0 0 4px;

      line-height: 1;
      font-size: 14px;
    }
  }
`

const StyledSlider = styled(Slider)`
  &.ant-slider {
    width: 100%;
    margin: 10px 8px 10px 2px;
  }

  &:hover {
    .ant-slider-handle[role='slider'] {
      background: white;
      border-color: #4a4a68;
    }
  }

  .ant-slider-rail {
    height: 6px;

    background-color: #f3f2f4;
    border-radius: 8px;
  }

  .ant-slider-track {
    height: 6px;
  }

  .ant-slider-step {
    display: none;
  }

  .ant-slider-mark {
    top: 16px;
  }

  .ant-slider-handle[role='slider'] {
    top: -2px;
    margin-top: 0;
    width: 16px;
    height: 16px;

    border: 3px solid #4a4a68;
    box-shadow: 0px 1px 2px 0px #4a4a681a;

    &:hover {
      border-color: #4a4a68;
      box-shadow: 0px 1px 2px 0px #4a4a681a;
      background: white;
    }
  }
`

const StyledButton = styled(Button)`
  display: flex;
  border: none;
  padding: 8px;

  svg {
    color: #d092dd;
    transition: color 0.2s ease-in-out;
  }

  svg path {
    stroke-width: 1.5;
  }

  &:hover {
    svg {
      color: #4a4a68;
    }
  }
`

const StyledSuffixedInput = styled(Input)`
  height: 37.81px;
  padding-left: 12px;

  .ant-input,
  input {
    text-align: right;
  }

  .ant-input:hover {
    box-shadow: none;
  }

  .ant-input-suffix {
    line-height: 1;
  }
`

const InputNumberContainer = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .ant-form-item {
    width: 100%;
  }

  .ant-input-disabled {
    background-color: white;
    border: 1px solid #dbd9df;

    &::placeholder {
      color: #4a4a68;
    }
  }
`

const InputNumberSuffix = styled.div<{ fieldName: number }>`
  ${({ fieldName }) => `
  position: absolute;
  top: ${fieldName === 0 ? '44px' : '4px'};
  right: 3px;
  z-index: 2;

  .ant-btn.ant-btn-text,
  .ant-btn.ant-btn-text:hover {
    border: none;
    background: transparent;

    svg {
      fill: #8c8ca1;
    }
  }
  `}
`

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  border: 1px solid #dbd9df;
  border-radius: 4px;

  .ant-input-number-input {
    color: #4a4a68;
  }

  .ant-input-number-prefix {
    margin-right: 0;
    font-size: 14px;

    span {
      line-height: 1;
    }
  }

  &:hover {
    &.ant-input-number-affix-wrapper {
      border: 1px solid #4a4a68 !important;
      box-shadow: 0px 0px 0px 4px rgba(109, 118, 236, 0.2);
    }
  }

  .ant-input-number-handler-wrap {
    display: none;
  }

  .ant-input-number:hover {
    border: none;
    box-shadow: none;
  }
`

const NoRules = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 12px;

  h3 {
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
    color: #4a4a68;
  }

  p {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.6;
    color: #4a4a68;
    max-width: 52ch;
    text-align: center;
  }
`

const DefaultPrepTimeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  span {
    color: #4a4a68;
    font-size: 14px;
    font-weight: 400;
    letter-spacing: 0.2px;
  }
`

export default PreparationTime
